import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Truck } from 'shared/icons';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import { Mono } from '../../ui/Mono';
import {
    BigAction,
    CodeCard,
    DangerScreenAction,
    FailureScreen,
    PlainAddress,
    ScanFlash,
    StepShell,
    SuccessScreen,
    WaitScreen,
} from '../collector/CollectorChrome';
import ScanInput from '../collector/ScanInput';
import { PlayMovementDone, PlayScanError, PlayScanSuccess } from '../collector/collectorFeedback';
import { CodesMatch, SCAN_METHOD_SCANNED, SCAN_METHOD_TYPED } from '../collector/collectorScan';
import { ExtractListRows } from '../payload';
import {
    FormatProgressText,
    IsAlreadyLoadedScan,
    IsNotInChecklistError,
    IsShipmentNotOpenError,
    LoadedChecklistLines,
    MissingChecklistLines,
    ParseShipmentProgress,
} from './shipmentModel';
import { GetShipment, ListShipments, ScanShipmentItem } from './shippingApi';

/**
 * CARREGAMENTO NO CHÃO (M8) — o bipe de cada pallet/volume contra o checklist
 * do romaneio, na gramática do M4/M5.
 *
 * ── ⚠️ UM BIPE POR ITEM (dono, 13/08) ──────────────────────────────────────
 * Palavras dele: *"Não precisa bipar a doca para embarcar, só quando for tirado
 * do estoque e levado para a Doca é que precisa bipar para falar que o LPN está
 * na doca."*
 *
 * O operador bipa a etiqueta do PALLET (LPN) ou do VOLUME (etiqueta do ML), e
 * pronto: a linha vira `LOADED`. O segundo passo — bipar a `DCA-80X` — MORREU
 * aqui. Levar o pallet até a doca é uma movimentação normal (M4/M5), que já bipa
 * o endereço e já grava trilha; repetir o bipe no carregamento era cobrar duas
 * leituras por item para registrar um fato que o sistema já tinha.
 *
 * A doca não sumiu da tela: ela continua identificando O CAMINHÃO na escolha do
 * embarque ("qual é o meu") e no fim do carregamento. O que ela não é mais é uma
 * LEITURA — e onde o pallet estava vai registrado sozinho, derivado da posição
 * dele no instante do bipe (`scanned_location_code`, do backend).
 *
 * ── O que fica na tela o tempo todo ────────────────────────────────────────
 * O progresso ("7 de 9 bipados") e a LISTA DO QUE FALTA. Quem está carregando
 * precisa saber qual etiqueta procurar no próximo pallet — esconder isso atrás
 * de um clique transformaria o coletor num contador de bipes.
 *
 * ── Os 409 que sobraram, tratados um a um ──────────────────────────────────
 *   - **carga que não é deste romaneio** → tela VERMELHA com o que foi lido e a
 *     lista do que ainda falta (é o erro perigoso: carregar no caminhão errado);
 *   - **embarque não está mais aberto** → ÂMBAR, não vermelho: o operador não
 *     errou, o Supervisor fechou/cancelou enquanto ele carregava.
 * E o **bipe repetido**, que o backend responde 200 e idempotente, é um aviso
 * âmbar de uma linha — pintar rotina de vermelho ensina o chão a ignorar
 * vermelho.
 */

const PHASE = {
    CHOOSE: 'CHOOSE',
    SCAN_ITEM: 'SCAN_ITEM',
    MISMATCH: 'MISMATCH',
    NOTICE: 'NOTICE',
    DONE: 'DONE',
};

/**
 * O QUE FALTA, em tamanho de doca. A REGRA (quais linhas faltam) é a
 * compartilhada `MissingChecklistLines`; o que muda aqui é só a tipografia —
 * chip grande e monoespaçado, legível com luva e a um braço de distância.
 */
const MissingStrip = ({ shipment, label }) => {
    const missing = MissingChecklistLines(shipment);
    const progress = ParseShipmentProgress(shipment);

    return (
        <div className="space-y-2 rounded-2xl border-2 border-mono-200 bg-mono-50 px-3 py-3 dark:border-mono-800 dark:bg-mono-900">
            <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-bold uppercase tracking-wide text-mono-600 dark:text-mono-300">
                    {label('wms.shipment.collector.missing_title')}
                </span>
                <Badge variant={progress.complete ? 'success' : 'warning'}>
                    {FormatProgressText(progress, label)}
                </Badge>
            </div>
            {progress.total === 0 ? (
                // Checklist VAZIO não é "tudo bipado": o embarque não fecha
                // assim (EMPTY_CHECKLIST), e dizer ao operador que terminou o
                // mandaria embora de uma doca onde nada foi conferido.
                <p className="text-sm font-semibold text-warning-text dark:text-warning-300">
                    {label('wms.shipment.empty_checklist')}
                </p>
            ) : missing.length === 0 ? (
                <p className="text-sm font-semibold text-success-text dark:text-success-300">
                    {label('wms.shipment.collector.missing_none')}
                </p>
            ) : (
                <ul className="flex max-h-40 flex-wrap gap-2 overflow-y-auto">
                    {missing.map((line, index) => (
                        <li
                            key={line.id || `${line.code}-${index}`}
                            className="rounded-lg border border-mono-300 bg-white px-2 py-1 dark:border-mono-700 dark:bg-mono-950"
                        >
                            <Mono className="text-base font-bold text-black dark:text-white">{line.code}</Mono>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const ShipmentLoadingFlow = ({ label, operator, onExit }) => {
    const [phase, setPhase] = useState(PHASE.CHOOSE);
    const [openShipments, setOpenShipments] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [shipment, setShipment] = useState(null);
    const [mismatch, setMismatch] = useState(null);
    const [notice, setNotice] = useState(null);
    const [busy, setBusy] = useState(false);
    const [flash, setFlash] = useState(null);

    const loadOpenShipments = useCallback(async () => {
        setListLoading(true);
        try {
            const data = await ListShipments({ status: 'OPEN', limit: 50 });
            setOpenShipments(ExtractListRows(data, ['shipments']));
        } catch {
            setOpenShipments([]);
        } finally {
            setListLoading(false);
        }
    }, []);

    useEffect(() => {
        if (phase === PHASE.CHOOSE) {
            loadOpenShipments();
        }
    }, [loadOpenShipments, phase]);

    /** A LISTA vem sem checklist (resumo); quem sabe o que falta é a FICHA. */
    const pickShipment = async (row) => {
        setBusy(true);
        try {
            const detail = await GetShipment(row.id);
            setShipment(detail);
            setPhase(PHASE.SCAN_ITEM);
        } catch (error) {
            PlayScanError();
            setMismatch({
                title: label('wms.collector.scan_failed_title'),
                message: error?.message || label('wms.common.load_error'),
                retryPhase: PHASE.CHOOSE,
            });
            setPhase(PHASE.MISMATCH);
        } finally {
            setBusy(false);
        }
    };

    const backToItem = () => {
        setMismatch(null);
        setNotice(null);
        setPhase(PHASE.SCAN_ITEM);
    };

    const backToChoose = () => {
        setShipment(null);
        setMismatch(null);
        setNotice(null);
        setPhase(PHASE.CHOOSE);
    };

    /** "Tentar de novo": trocar de embarque ou voltar a bipar carga. */
    const retryFromMismatch = () => {
        if (mismatch?.retryPhase === PHASE.CHOOSE) {
            backToChoose();
            return;
        }
        backToItem();
    };

    // ── O BIPE ÚNICO: a etiqueta da CARGA, e é ele que grava ────────────────
    //
    // A ÚNICA conferência local é o item que já está `LOADED`: `LOADED` é
    // terminal (linha bipada não volta a PENDING nem pode ser removida), então
    // esta leitura nunca produz um "não" que o servidor diria "sim". Ela poupa
    // uma ida ao servidor num gesto que é rotina na doca — e a resposta é a
    // MESMA que o servidor daria: aviso âmbar, sem movimento.
    //
    // O resto é do BACKEND: quem decide se o código pertence ao checklist é ele
    // (§2.2 — o parsing e a igualdade exata moram num lugar só), e é ele que
    // registra de onde o pallet saiu, derivando a posição sem perguntar nada.
    const handleItemScan = async (code, scanMethod) => {
        if (!shipment?.id) {
            return;
        }

        const alreadyLoadedLocally = LoadedChecklistLines(shipment).some((line) => CodesMatch(line.code, code));
        if (alreadyLoadedLocally) {
            PlayScanSuccess();
            setNotice({
                title: label('wms.shipment.collector.already_loaded_title'),
                message: label('wms.shipment.collector.already_loaded_body'),
            });
            setPhase(PHASE.NOTICE);
            return;
        }

        setBusy(true);
        const method = scanMethod === SCAN_METHOD_TYPED ? SCAN_METHOD_TYPED : SCAN_METHOD_SCANNED;
        try {
            const payload = await ScanShipmentItem(shipment.id, {
                code,
                scan_method: method,
                // QUEM FEZ: o crachá da sessão. A conta do coletor é
                // COMPARTILHADA — sem isto a autoria seria a do dispositivo.
                operator_code: operator?.employee_code || undefined,
            });
            setShipment(payload);

            if (IsAlreadyLoadedScan(payload)) {
                // Bipe repetido NÃO leva o som de falha: o item está no
                // caminhão, ninguém errou, e nenhum movimento foi gravado. O
                // aviso âmbar de tela cheia é o que marca a diferença.
                PlayScanSuccess();
                setNotice({
                    title: label('wms.shipment.collector.already_loaded_title'),
                    message: label('wms.shipment.collector.already_loaded_body'),
                });
                setPhase(PHASE.NOTICE);
                return;
            }

            const progress = ParseShipmentProgress(payload);
            if (progress.complete) {
                PlayMovementDone();
                setPhase(PHASE.DONE);
                return;
            }
            PlayScanSuccess();
            setFlash(label('wms.shipment.collector.flash_loaded'));
            setPhase(PHASE.SCAN_ITEM);
        } catch (error) {
            PlayScanError();

            if (IsShipmentNotOpenError(error)) {
                setNotice({
                    title: label('wms.shipment.collector.not_open_title'),
                    message: label('wms.shipment.collector.not_open_body'),
                    terminal: true,
                });
                setPhase(PHASE.NOTICE);
                return;
            }

            if (IsNotInChecklistError(error)) {
                setMismatch({
                    title: label('wms.shipment.collector.not_in_checklist_title'),
                    message: label('wms.shipment.collector.not_in_checklist_body'),
                    read: code,
                    // O que ERA esperado: as linhas que ainda faltam. Sem isto a
                    // tela diria "não é daqui" sem dizer o que é daqui.
                    reasons: MissingChecklistLines(shipment).map((line) => ({
                        rule: label(`wms.shipment_item_kind.${line.kind}`),
                        message: line.code,
                    })),
                    retryPhase: PHASE.SCAN_ITEM,
                });
                setPhase(PHASE.MISMATCH);
                return;
            }

            setMismatch({
                title: label('wms.collector.scan_failed_title'),
                message: error?.message || label('wms.common.load_error'),
                read: code,
                retryPhase: PHASE.SCAN_ITEM,
            });
            setPhase(PHASE.MISMATCH);
        } finally {
            setBusy(false);
        }
    };

    const flashNode = <ScanFlash message={flash} onDone={() => setFlash(null)} />;

    // ── Telas de exceção ────────────────────────────────────────────────────
    if (phase === PHASE.MISMATCH) {
        return (
            <>
                {flashNode}
                <FailureScreen
                    label={label}
                    title={mismatch?.title}
                    message={mismatch?.message}
                    expected={mismatch?.expected}
                    read={mismatch?.read}
                    reasons={mismatch?.reasons || []}
                    actions={
                        <>
                            <BigAction variant="secondary" onClick={retryFromMismatch}>
                                {label('wms.collector.try_again')}
                            </BigAction>
                            <DangerScreenAction onClick={backToChoose}>
                                {label('wms.shipment.collector.change_shipment')}
                            </DangerScreenAction>
                        </>
                    }
                />
            </>
        );
    }

    if (phase === PHASE.NOTICE) {
        return (
            <WaitScreen
                title={notice?.title}
                message={notice?.message}
                actions={
                    <>
                        {notice?.terminal ? null : (
                            <BigAction onClick={backToItem}>{label('wms.collector.try_again')}</BigAction>
                        )}
                        <Button variant="ghost" size="lg" className="w-full" onClick={backToChoose}>
                            {label('wms.shipment.collector.change_shipment')}
                        </Button>
                    </>
                }
            >
                {notice?.terminal ? null : <MissingStrip shipment={shipment} label={label} />}
            </WaitScreen>
        );
    }

    if (phase === PHASE.DONE) {
        return (
            <>
                {flashNode}
                <SuccessScreen
                    title={label('wms.shipment.collector.done_title')}
                    actions={
                        <>
                            <BigAction onClick={backToChoose}>
                                {label('wms.shipment.collector.change_shipment')}
                            </BigAction>
                            <Button variant="ghost" size="lg" className="w-full" onClick={onExit}>
                                {label('wms.collector.back_to_menu')}
                            </Button>
                        </>
                    }
                >
                    <p className="text-center text-lg font-semibold text-success-text dark:text-success-200">
                        {label('wms.shipment.collector.done_body')}
                    </p>
                    <CodeCard caption={label('wms.shipment.collector.dock_of')} tone="success">
                        {shipment?.dock_code}
                    </CodeCard>
                </SuccessScreen>
            </>
        );
    }

    // ── Passo 0: qual embarque ──────────────────────────────────────────────
    if (phase === PHASE.CHOOSE) {
        return (
            <StepShell
                title={label('wms.shipment.collector.choose_title')}
                footer={
                    <Button variant="ghost" size="lg" className="w-full" onClick={onExit}>
                        <ArrowLeft size={20} className="mr-2" />
                        {label('wms.collector.back_to_menu')}
                    </Button>
                }
            >
                {listLoading ? (
                    <p className="text-center text-base text-mono-500">{label('wms.collector.waiting_scan')}</p>
                ) : openShipments.length === 0 ? (
                    <p className="rounded-2xl border-2 border-mono-200 px-4 py-6 text-center text-base text-mono-500 dark:border-mono-800">
                        {label('wms.shipment.collector.choose_empty')}
                    </p>
                ) : (
                    <div className="space-y-3">
                        {openShipments.map((row) => {
                            const progress = ParseShipmentProgress(row);
                            return (
                                <button
                                    key={row.id}
                                    type="button"
                                    disabled={busy}
                                    onClick={() => pickShipment(row)}
                                    className="w-full rounded-2xl border-2 border-mono-300 bg-white px-4 py-4 text-left transition-colors hover:bg-mono-50 disabled:opacity-60 dark:border-mono-700 dark:bg-mono-950 dark:hover:bg-mono-900"
                                >
                                    <span className="flex items-center gap-3">
                                        <Truck size={26} className="shrink-0 text-mono-500" />
                                        <span className="min-w-0 flex-1">
                                            <Mono className="block text-2xl font-black text-black dark:text-white">
                                                {row.dock_code}
                                            </Mono>
                                            <span className="block text-sm text-mono-500 dark:text-mono-400">
                                                {row.outbound?.vehicle_plate || ''}
                                            </span>
                                        </span>
                                        <Badge variant={progress.complete ? 'success' : 'warning'}>
                                            {FormatProgressText(progress, label)}
                                        </Badge>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </StepShell>
        );
    }

    // ── O bipe ──────────────────────────────────────────────────────────────
    // SEM barra de passos: com uma leitura só, "passo 1 de 1" seria um progresso
    // que nunca anda — chrome ocupando a tela do coletor sem informar nada.
    return (
        <>
            {flashNode}
            <StepShell
                footer={
                    <>
                        <Button
                            variant="secondary"
                            size="lg"
                            className="w-full"
                            onClick={backToChoose}
                            disabled={busy}
                        >
                            <ArrowLeft size={20} className="mr-2" />
                            {label('wms.shipment.collector.change_shipment')}
                        </Button>
                        <Button variant="ghost" size="lg" className="w-full" onClick={onExit} disabled={busy}>
                            {label('wms.collector.back_to_menu')}
                        </Button>
                    </>
                }
            >
                <PlainAddress>{label('wms.shipment.collector.step_item')}</PlainAddress>
                <p className="text-center text-base text-mono-600 dark:text-mono-400">
                    {label('wms.shipment.collector.step_item_hint')}
                </p>
                {/* A doca fica à vista como CONTEXTO — "é este o caminhão" —, e
                    não como algo a bipar: desde 13/08 ela é a posição do pallet,
                    e o operador não lê etiqueta nenhuma de doca aqui. */}
                <CodeCard caption={label('wms.shipment.collector.dock_of')}>{shipment?.dock_code}</CodeCard>
                <ScanInput
                    label={label}
                    onScan={handleItemScan}
                    disabled={busy}
                    focusKey={phase}
                    manualPlaceholder="REV-260812-001"
                />
                <MissingStrip shipment={shipment} label={label} />
            </StepShell>
        </>
    );
};

export default ShipmentLoadingFlow;
